import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronDown, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function GroupCard({ group, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ marginBottom: 12, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }} data-testid={`blocked-group-${group.group_name}`}>
      {/* Card header */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid={`blocked-group-toggle-${group.group_name}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%', padding: '10px 16px',
          background: '#f8fafc', border: 'none',
          cursor: 'pointer', textAlign: 'left',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; }}
      >
        <ChevronDown
          size={15}
          style={{
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s',
            color: '#64748b', flexShrink: 0,
          }}
        />
        <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 700, color: '#0f172a', flex: 1 }}>
          {group.group_name}
        </span>

        {/* Redirect URL chip */}
        {group.redirect_url && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontFamily: 'monospace',
            background: '#eff6ff', color: '#3182ce',
            padding: '2px 8px', borderRadius: 6,
            maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <ExternalLink size={10} />
            {group.redirect_url}
          </span>
        )}

        {/* Domain count badge */}
        <span style={{
          fontSize: 11, fontWeight: 700,
          background: '#f1f5f9', color: '#64748b',
          padding: '2px 9px', borderRadius: 10, flexShrink: 0,
        }}>
          {group.domain_count.toLocaleString()} domain{group.domain_count !== 1 ? 's' : ''}
        </span>
      </button>

      {/* Domain list */}
      {open && (
        <div style={{ borderTop: '1px solid #e2e8f0' }}>
          {group.domains.length === 0 ? (
            <div style={{ padding: '12px 16px', color: '#718096', fontSize: 13 }}>No domains in this group.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: '6px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#718096', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>DOMAIN</th>
                  <th style={{ padding: '6px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#718096', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>REASON</th>
                  <th style={{ padding: '6px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#718096', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>ADDED BY</th>
                </tr>
              </thead>
              <tbody>
                {group.domains.map((d, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }} data-testid={`blocked-domain-row-${group.group_name}-${i}`}>
                    <td style={{ padding: '7px 16px', fontFamily: 'monospace', fontSize: 12, color: '#0f172a' }}>{d.domain}</td>
                    <td style={{ padding: '7px 16px', fontSize: 12, color: '#475569' }}>{d.reason || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    <td style={{ padding: '7px 16px', fontSize: 12, color: '#475569' }}>{d.added_by || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function BlockedDomainsPage() {
  const navigate = useNavigate();
  const [groups,  setGroups]  = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [search,  setSearch]  = useState('');
  const [applied, setApplied] = useState('');

  const fetchData = async (q) => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      const r = await axios.get(`${API}/api/partners/blocked-domains`, { params, withCredentials: true });
      setGroups(r.data.groups || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(''); }, []); // eslint-disable-line

  const handleSearch = () => { setApplied(search); fetchData(search); };
  const handleReset  = () => { setSearch(''); setApplied(''); fetchData(''); };

  return (
    <div data-testid="blocked-domains-page">
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt;{' '}
        <span style={{ cursor: 'pointer', color: '#3182ce' }} onClick={() => navigate('/partners')} data-testid="blocked-back-link">
          Partner Dashboard
        </span>{' '}
        &gt; Blocked Domains
      </div>

      <div className="page-header">
        <h1>Blocked Domains</h1>
        <p>{total > 0 ? `${total.toLocaleString()} total blocked domains across ${groups.length} group${groups.length !== 1 ? 's' : ''}` : 'Domains blocked from registration'}</p>
      </div>

      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group" style={{ flex: 3 }}>
          <label className="filter-label">Search domain or group</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              className="filter-input"
              style={{ paddingLeft: 30 }}
              placeholder="Search by domain or group name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              data-testid="blocked-search-input"
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary"   onClick={handleSearch} data-testid="blocked-search-btn">Search</button>
          <button className="btn btn-secondary" onClick={handleReset}  data-testid="blocked-reset-btn">Reset</button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : groups.length === 0 ? (
        <div className="empty-state">
          <h3>No blocked domains found</h3>
          <p>{applied ? 'Try a different search term' : 'Table appears to be empty'}</p>
        </div>
      ) : (
        <div data-testid="blocked-groups-container">
          {groups.map((g, i) => (
            <GroupCard key={g.group_name} group={g} defaultOpen={i === 0} />
          ))}
        </div>
      )}
    </div>
  );
}
